import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, todayYmd } from "@/lib/api";
import { PageHeader, StatCard, StageBadge } from "@/components/app/UI";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Bell, Flame, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";

async function exportAlerts(list) {
  try {
    const r = await api.get("/dashboard/alerts-export", { params: { list }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard-alerts-${list}-${todayYmd()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export failed");
  }
}

function DelayCard({ testid, title, subtitle, tone, items, when, exportKey }) {
  const borderColor = tone === "danger" ? "border-l-[#DC2626]" : "border-l-[#F59E0B]";
  const iconColor = tone === "danger" ? "text-[#DC2626]" : "text-[#F59E0B]";
  return (
    <div className={`flat-card p-5 border-l-4 ${borderColor}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className={`w-4 h-4 ${iconColor}`} />
        <div className="label-eyebrow">{title}</div>
        <span className={`ml-auto text-[10px] uppercase tracking-widest font-bold ${iconColor}`}>{items.length}</span>
        {exportKey && items.length > 0 && (
          <button onClick={() => exportAlerts(exportKey)} title="Export to Excel"
            className="p-1 text-[#4B5563] hover:text-[#002FA7] transition-colors" data-testid={`${testid}-export`}>
            <Download className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-3">{subtitle}</div>
      {items.length === 0 ? (
        <div className="text-xs text-[#9CA3AF] italic py-2">All clear.</div>
      ) : (
        <div className="space-y-1 max-h-80 overflow-y-auto pr-1">
          {items.map((p) => (
            <Link
              key={p.project_id}
              to={`/projects/${p.project_id}`}
              className="flex items-center justify-between text-sm border-b border-[#F3F4F6] py-1.5 last:border-0 hover:bg-[#F9FAFB] px-1 -mx-1"
              data-testid={`${testid}-row-${p.project_id}`}
            >
              <div className="min-w-0 flex-1 truncate">
                <span className="font-semibold">{p.customer_name}</span>
                <span className="text-[10px] text-[#9CA3AF] ml-2 font-mono">{p.qr_code}</span>
              </div>
              <div className={`text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${iconColor}`}>{when(p)}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [crm, setCrm] = useState(null);
  const [todayLeads, setTodayLeads] = useState([]);
  const [delays, setDelays] = useState({ documentation_delay: [], first_payment_delay: [], checklist_missing_delay: [], second_payment_delay: [], reschedule_slip: [] });
  const [feasAlerts, setFeasAlerts] = useState([]);

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data));
    api.get("/leads/dashboard/metrics").then((r) => setCrm(r.data));
    api.get("/leads/followups/today").then((r) => setTodayLeads(r.data));
    api.get("/dashboard/project-delays").then((r) => setDelays(r.data)).catch(() => {});
    api.get("/dashboard/feasibility-alerts").then((r) => setFeasAlerts(r.data || [])).catch(() => {});
  }, []);

  if (!data) return <div className="p-4 sm:p-8 label-eyebrow">Loading…</div>;

  const today = todayYmd();
  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Command Center"
        title="Operations Overview"
        subtitle="Real-time visibility from lead capture to after-sales — across sales, engineering, installation, and service."
      />

      <div className="p-4 sm:p-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <StatCard testid="stat-leads" label="Total Leads" value={data.leads_total} sub={`${data.conversion_pct}% conversion`} />
          <StatCard testid="stat-active" label="Active Projects" value={data.active_projects} sub={`${data.completed_projects} completed`} accent="warning" />
          <StatCard testid="stat-tickets" label="Open Tickets" value={data.open_tickets} sub={`avg resolution ${data.avg_resolution_hours} h`} accent="danger" />
        </div>

        {crm && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8" data-testid="crm-metrics">
            <CRMStat label="New Today" value={crm.new_leads_today} testid="crm-new-today" />
            <CRMStat label="Today's FU" value={crm.todays_followups} testid="crm-today-fu" accent={crm.todays_followups > 0 ? "warn" : ""} />
            <CRMStat label="Overdue" value={crm.overdue_followups} testid="crm-overdue" accent={crm.overdue_followups > 0 ? "danger" : ""} />
            <CRMStat label={<span className="flex items-center gap-1"><Flame className="w-3 h-3" /> Super Hot</span>} value={crm.super_hot_leads} testid="crm-super-hot" accent="danger" />
            <CRMStat label="Won This Month" value={crm.won_this_month} testid="crm-won-month" accent="success" />
            <CRMStat label="Total Leads" value={crm.total_leads} testid="crm-total" />
          </div>
        )}

        {/* Top alerts — Feasibility Expiring / 1st Payment / Checklist Missing */}
        {(feasAlerts.length + delays.first_payment_delay.length + (delays.checklist_missing_delay?.length || 0)
          + delays.documentation_delay.length + delays.second_payment_delay.length + (delays.reschedule_slip?.length || 0)) > 0 && (
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow">Project Alerts</div>
            <button onClick={() => exportAlerts("all")} data-testid="alerts-export-all"
              className="border border-[#0a0a0a] uppercase tracking-widest text-[10px] font-bold h-8 px-3 flex items-center gap-1.5 hover:bg-[#0a0a0a] hover:text-white transition-colors">
              <Download className="w-3.5 h-3.5" /> Export All Lists
            </button>
          </div>
        )}
        {(feasAlerts.length + delays.first_payment_delay.length + (delays.checklist_missing_delay?.length || 0)) > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="dashboard-top-alerts">
            <DelayCard
              testid="feasibility-alerts-panel"
              exportKey="feasibility"
              title="Feasibility Expiring"
              subtitle="KSEB feasibility approval expiring within 15 days, overdue, or expiry date not set"
              tone="danger"
              items={feasAlerts.map((p) => ({
                project_id: p.id, customer_name: p.customer_name, qr_code: p.qr_code,
                _days_left: p.days_left, _is_overdue: p.is_overdue, _expiry: p.feasibility_expiry_date,
              }))}
              when={(p) => p._days_left === null || p._days_left === undefined ? "date not set" : p._is_overdue ? `overdue ${Math.abs(p._days_left)}d` : `${p._days_left}d left`}
            />
            <DelayCard
              testid="delay-first-payment"
              exportKey="first_payment"
              title="1st Payment Delayed"
              subtitle="Token received > 10 days, 1st payment pending"
              tone="danger"
              items={delays.first_payment_delay}
              when={(p) => `token ${p.days_pending}d ago`}
            />
            <DelayCard
              testid="delay-checklist-missing"
              exportKey="checklist_missing"
              title="Delivery Checklist Missing"
              subtitle="Advance paid ≥ 2 days, no delivery checklist yet"
              tone="danger"
              items={delays.checklist_missing_delay || []}
              when={(p) => `advance ${p.days_pending}d ago`}
            />
          </div>
        )}

        {/* Secondary alerts — Documentation Delay / 2nd Payment / Repeated Reschedules */}
        {(delays.documentation_delay.length + delays.second_payment_delay.length + (delays.reschedule_slip?.length || 0)) > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="dashboard-secondary-alerts">
            <DelayCard
              testid="delay-documentation"
              exportKey="documentation"
              title="Documentation Delay"
              subtitle="Stage 03 in progress > 8 days"
              tone="warn"
              items={delays.documentation_delay}
              when={(p) => `started ${p.days_pending}d ago`}
            />
            <DelayCard
              testid="delay-second-payment"
              exportKey="second_payment"
              title="2nd Payment Delayed"
              subtitle="Material delivered > 7 days, 2nd payment pending"
              tone="danger"
              items={delays.second_payment_delay}
              when={(p) => `delivered ${p.days_pending}d ago`}
            />
            <DelayCard
              testid="delay-reschedule-slip"
              exportKey="reschedule"
              title="Rescheduled ≥ 2 Times"
              subtitle="Open delivery checklists that keep slipping"
              tone="warn"
              items={(delays.reschedule_slip || []).map((r) => ({
                ...r,
                // DelayCard uses `project_id` for the link; the row displays
                // the checklist number instead of the plain QR so operators
                // can jump straight to the offending delivery.
                qr_code: r.checklist_no || r.qr_code,
              }))}
              when={(p) => `${p.reschedule_count}× rescheduled`}
            />
          </div>
        )}

        {todayLeads.length > 0 && (
          <div className="flat-card p-4 sm:p-6 mb-8" data-testid="today-followups-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#F59E0B]" />
                <h3 className="font-black text-lg tracking-tight">Today's Follow-Ups</h3>
                <span className="text-xs text-[#4B5563]">{todayLeads.length} pending</span>
              </div>
              <Link to="/leads" state={{ view: "today" }} className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline">Open Leads →</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr>{["Lead", "Phone", "Assignee", "Status", "Time"].map((h) => <th key={h} className="text-left label-eyebrow !text-[9px] px-3 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {todayLeads.slice(0, 8).map((l) => {
                    const overdue = l.next_followup_date && l.next_followup_date < today;
                    return (
                      <tr key={l.id} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] ${overdue ? "bg-[#FEF2F2]" : ""}`}>
                        <td className="px-3 py-2"><Link to={`/leads/${l.id}`} className="font-semibold hover:underline">{l.name}</Link>{overdue && <span className="ml-2 text-[9px] uppercase font-bold text-[#DC2626]">overdue</span>}</td>
                        <td className="px-3 py-2 text-xs font-mono">{l.phone}</td>
                        <td className="px-3 py-2 text-xs">{l.assignee_name || "—"}</td>
                        <td className="px-3 py-2"><StageBadge stage={l.status || l.stage} /></td>
                        <td className="px-3 py-2 text-xs">{l.next_followup_time || "—"}</td>
                      </tr>
                    );
                  })}
                  {todayLeads.length > 8 && <tr><td colSpan={5} className="text-center text-xs text-[#4B5563] py-2"><Link to="/leads" className="hover:underline">+ {todayLeads.length - 8} more…</Link></td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flat-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">Sales Pipeline</div>
                <h3 className="text-xl font-black tracking-tight">Conversion Funnel</h3>
              </div>
              <div className="text-xs text-[#4B5563]">All time</div>
            </div>
            <div style={{ width: "100%", height: 280, minWidth: 0 }}>
              <ResponsiveContainer width="99%" height={280}>
                <BarChart data={data.funnel} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#4B5563" }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5563" }} />
                  <Tooltip cursor={{ fill: "#F3F4F6" }} contentStyle={{ borderRadius: 0, border: "1px solid #0a0a0a" }} />
                  <Bar dataKey="count" fill="#002FA7">
                    {data.funnel.map((entry, idx) => (
                      <Cell key={entry.stage} fill={entry.stage === "Won" ? "#10B981" : entry.stage === "Lost" ? "#EF4444" : "#002FA7"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="flat-card p-6">
            <div className="label-eyebrow mb-1">Service Desk</div>
            <h3 className="text-xl font-black tracking-tight mb-6">Tickets by status</h3>
            <div className="space-y-3">
              {Object.entries(data.tickets_by_status).map(([k, v]) => {
                const max = Math.max(1, ...Object.values(data.tickets_by_status));
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-[#0a0a0a]">{k}</span>
                      <span className="text-[#4B5563]">{v}</span>
                    </div>
                    <div className="h-2 bg-[#F3F4F6]">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${(v / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CRMStat({ label, value, sub, accent, testid }) {
  const tone = accent === "danger" ? "border-[#DC2626] text-[#DC2626]"
             : accent === "warn" ? "border-[#F59E0B] text-[#F59E0B]"
             : accent === "success" ? "border-[#10B981] text-[#10B981]"
             : "border-[#0a0a0a] text-[#0a0a0a]";
  return (
    <div className={`border ${tone} p-3 bg-white`} data-testid={testid}>
      <div className="label-eyebrow !text-[9px] text-[#4B5563]">{label}</div>
      <div className={`text-2xl font-black tracking-tight mt-1 ${tone.split(" ")[1]}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{sub}</div>}
    </div>
  );
}
